import { useState, useRef, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  IconButton,
  MenuItem,
  Dialog,
  Tooltip,
  alpha,
} from '@mui/material';
import Fade from '@mui/material/Fade';
import KeyboardArrowDownRounded from '@mui/icons-material/KeyboardArrowDownRounded';
import Sidebar from '../../Pages/landingPage/Sidebar';
import {
  Announcement,
  Person4,
  SettingsRounded,
  TrackChanges,
  Business,
  Campaign,
  SignalWifiStatusbar4Bar,
  Notifications,
  TrendingUp,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { AdminTabs } from '../AdminTabs';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import { styled } from '@mui/system';
import EditIcon from '../../Images/Edit.png';
import DeleteIcon from '../../Images/Delete.png';
import ViewIcon from '../../Images/View.png';
import Cookies from 'js-cookie';
import {
  createAnnouncement,
  getAnnouncements,
  deleteAnnouncement,
  getAnnouncementById,
  updateAnnouncement,
} from '../../apis/Admin';
import { useTimezone } from '../../hooks/useTimezone';
// eslint-disable-next-line no-unused-vars
import OnboardingTour from '../../components/OnboardingTour';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import DOMPurify from 'dompurify';
import { DataTable } from '../../components/ui/table';
import { PageHeader } from '../../components/ui/header';
import { SummaryCard } from '../../components/ui/card';
import PropTypes from 'prop-types';

const StyledTextField = styled(TextField)(({ multiline }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '4px',
    transition: 'all 0.2s ease-in-out',
    height: multiline ? 'auto' : 45,
    alignItems: 'flex-start',
    '& fieldset': {
      border: '0.5px solid #e5e7eb',
    },
    '&:hover fieldset': {
      border: '0.5px solid #d1d5db',
    },
    '&.Mui-focused fieldset': {
      border: '0.6px solid #1B2640',
    },
  },
  '& .MuiInputBase-input': {
    padding: multiline ? '10px 14px' : '10px 14px',
    fontSize: '0.9rem',
    fontWeight: 400,
    color: '#374151',
  },
  '& .MuiInputLabel-root': {
    color: '#1B2640',
    fontSize: '0.875rem',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#1B2640',
  },
  '& .MuiInputLabel-root.Mui-error': {
    color: '#d32f2f',
  },
}));

const Pill = ({ children, color }) => (
  <Box
    sx={{
      display: 'inline-block',
      px: 1.25,
      py: 0.35,
      borderRadius: '10px',
      fontSize: '0.75rem',
      fontWeight: 600,
      letterSpacing: '0.02em',
      color,
      bgcolor: alpha(color, 0.12),
      border: `1px solid ${color}`,
      textTransform: 'capitalize',
    }}
  >
    {children}
  </Box>
);

Pill.propTypes = {
  children: PropTypes.node.isRequired,
  color: PropTypes.string.isRequired,
};

const containerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.4, type: 'spring', stiffness: 100 },
  },
};

const rowVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: (Number(i) || 0) * 0.05,
      duration: 0.3,
      ease: 'easeOut',
    },
  }),
};

// simple word counter (treats sequences of non-space as words)
const countWords = (s) => {
  if (!s || !s.trim().length) return 0;
  const textOnly = s.replace(/<[^>]*>/g, ' ').trim();
  return textOnly ? textOnly.split(/\s+/).filter(Boolean).length : 0;
};

const sanitizeHtml = (html) => {
  if (!html) return '';
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'a', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
    ALLOWED_ATTR: ['href', 'target', 'rel'],
    ALLOW_DATA_ATTR: false,
  });
};

export default function AdminAnnoucement() {
  const { formatDate } = useTimezone();
  const [form, setForm] = useState({
    title: '',
    type: '',
    description: '',
    when: null,
    expiry_when: null,
    is_active: true,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // NEW: dialog state (form shown only in dialog)
  const [formOpen, setFormOpen] = useState(false);

  const [listLoading, setListLoading] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [deletingId, setDeletingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const isEditMode = Boolean(editingId);
  const listPaperRef = useRef(null);

  // 🔹 NEW: track which row is being toggled
  const [togglingId, setTogglingId] = useState(null);
  
  const [viewOpen, setViewOpen] = useState(false);
  const [viewData, setViewData] = useState(null);
  
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState('');

  const descriptionWords = useMemo(
    () => countWords(form.description || ''),
    [form.description],
  );

  const getDefaultExpiryDate = (scheduledDate) => {
    if (!scheduledDate) return null;
    const d = new Date(scheduledDate);
    d.setDate(d.getDate() + 7);
    return dayjs(d);
  };

  const rawRole = Cookies.get('Role_id') ?? Cookies.get('role_id');

  const isSuperAdmin = useMemo(() => {
    const n = Number(rawRole);
    return !Number.isNaN(n) && n === 1;
  }, [rawRole]);

  const scrollToRef = (ref, offset = 16) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = ref?.current;
        if (!el) return;
        const top = el.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top, behavior: 'smooth' });
      });
    });
  };

  const validate = () => {
    const e = {};
    if (!form.title.trim()) e.title = 'Title is required';
    if (!form.description.trim()) e.description = 'Description is required';
    if (descriptionWords > 250)
      e.description = `Maximum 250 words allowed (currently ${descriptionWords})`;
    if (!form.type) e.type = 'Type is required';
    if (!form.when) e.when = 'Date & Time is required';
    if (!form.expiry_when) {
      e.expiry_when = 'Expiry date is required';
    } else if (form.when && form.expiry_when && form.expiry_when.isBefore(form.when)) {
      e.expiry_when = 'Expiry date must be after scheduled date & time';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleDeleteAnnouncement = async (announcement_id) => {
    const prev = announcements; // snapshot for rollback
    // optimistic remove
    setAnnouncements((cur) =>
      cur.filter((a) => a.announcement_id !== announcement_id),
    );
    setDeletingId(announcement_id);

    try {
      const res = await deleteAnnouncement(announcement_id);
      toast.success(res?.message || 'Announcement deleted', {
        autoClose: 2000,
      });
    } catch (err) {
      // rollback
      setAnnouncements(prev);
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          'Failed to delete announcement',
      );
    } finally {
      setDeletingId(null);
    }
  };

  const set = (k) => (e) =>
    setForm((s) => ({ ...s, [k]: e && e.target ? e.target.value : e }));

  // fetch all announcements (optionally skip scroll)
  const handleAllAnnouncementClick = async ({ skipScroll = false } = {}) => {
    setListLoading(true);
    try {
      const res = await getAnnouncements();

      // normalize API shape → array
      const items = Array.isArray(res)
        ? res
        : Array.isArray(res?.announcements)
          ? res.announcements
          : [];

      // optional: newest first
      items.sort(
        (a, b) =>
          new Date(b.scheduled_datetime) - new Date(a.scheduled_datetime),
      );

      setAnnouncements(items);

      if (!skipScroll) {
        scrollToRef(listPaperRef, 16);
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message || 'Failed to fetch announcements',
      );
      setAnnouncements([]);
    } finally {
      setListLoading(false);
    }
  };

  // 🔹 Load table by default when tab is opened
  useEffect(() => {
    handleAllAnnouncementClick({ skipScroll: true });
  }, []);

  const handleToggleStatus = async (announcement) => {
    const { announcement_id } = announcement;
    const nextStatus = !announcement.is_active;

    // 🔹 Optimistic UI update
    const prev = announcements;
    setAnnouncements((cur) =>
      cur.map((a) =>
        a.announcement_id === announcement_id
          ? { ...a, is_active: nextStatus }
          : a,
      ),
    );
    setTogglingId(announcement_id);

    try {
      let expiryDateToUse = announcement.expiry_date;
      if (nextStatus) {
        const currentDate = new Date();
        const expiryDate = announcement.expiry_date 
          ? new Date(announcement.expiry_date) 
          : null;
        
        if (!expiryDate || expiryDate < currentDate) {
          const newExpiryDate = new Date();
          newExpiryDate.setDate(newExpiryDate.getDate() + 7);
          expiryDateToUse = newExpiryDate.toISOString();
        }
      }

      await updateAnnouncement(announcement_id, {
        title: announcement.title,
        announcement_type: announcement.announcement_type,
        description: announcement.description,
        scheduled_datetime: announcement.scheduled_datetime,
        expiry_date: expiryDateToUse,
        is_active: nextStatus,
      });

      toast.success(
        `Announcement marked as ${nextStatus ? 'Active' : 'Inactive'}.`,
        { autoClose: 1500 },
      );

      await handleAllAnnouncementClick({ skipScroll: true });
    } catch (err) {
      // rollback on error
      setAnnouncements(prev);
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          'Failed to update status',
      );
    } finally {
      setTogglingId(null);
    }
  };

  const onPublish = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const scheduledISO = form.when ? form.when.toDate().toISOString() : null;
      const expiryISO = form.expiry_when ? form.expiry_when.toDate().toISOString() : null;

      const sanitizedDescription = sanitizeHtml(form.description);

      const payload = {
        title: form.title.trim(),
        announcement_type: form.type, // match API key
        description: sanitizedDescription,
        scheduled_datetime: scheduledISO,
        expiry_date: expiryISO,
      };

      await createAnnouncement(payload);

      toast.success('Announcement published successfully.');
      await handleAllAnnouncementClick();
      setForm({ title: '', type: '', description: '', when: null, expiry_when: null, is_active: true });
      setErrors({});
      setFormOpen(false); // 🔹 close dialog after publish
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to publish announcement';
      setErrors((s) => ({ ...s, publish: msg }));
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const onCancel = () => {
    setErrors({});
    setEditingId(null);
    setForm({ title: '', type: '', description: '', when: null, expiry_when: null, is_active: true });
    setFormOpen(false); // 🔹 close dialog
  };

  const handleViewClick = async (announcement_id) => {
    try {
      const a = await getAnnouncementById(announcement_id);
      setViewData(a);
      setViewOpen(true);
    } catch (err) {
      toast.error(
        err?.response?.data?.message || 'Failed to load announcement',
      );
    }
  };

  const handleEditClick = async (announcement_id) => {
    try {
      const a = await getAnnouncementById(announcement_id);
      
      setForm({
        title: a?.title ?? '',
        type: a?.announcement_type ?? '',
        description: a?.description ?? '',
        when: a?.scheduled_datetime ? dayjs(a.scheduled_datetime) : null,
        expiry_when: a?.expiry_date ? dayjs(a.expiry_date) : null,
        is_active: a?.is_active ?? true,
      });
      setErrors({});
      setEditingId(announcement_id);
      setFormOpen(true); // 🔹 open dialog in edit mode
      toast.info('Loaded announcement for editing', { autoClose: 1200 });
    } catch (err) {
      toast.error(
        err?.response?.data?.message || 'Failed to load announcement',
      );
    }
  };

  const onUpdate = async () => {
    if (!isEditMode || !editingId) return;
    if (!validate()) return;
    setLoading(true);
    try {
      const scheduledISO = form.when ? form.when.toDate().toISOString() : null;
      const expiryISO = form.expiry_when ? form.expiry_when.toDate().toISOString() : null;

      const sanitizedDescription = sanitizeHtml(form.description);

      const payload = {
        title: form.title.trim(),
        announcement_type: form.type,
        description: sanitizedDescription,
        scheduled_datetime: scheduledISO,
        expiry_date: expiryISO,
        is_active: form.is_active,
      };

      await updateAnnouncement(editingId, payload);

      toast.success('Announcement updated successfully.');
      await handleAllAnnouncementClick();
      setEditingId(null);
      setForm({ title: '', type: '', description: '', when: null, expiry_when: null, is_active: true });
      setErrors({});
      setFormOpen(false); // 🔹 close dialog after update
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          'Failed to update announcement',
      );
    } finally {
      setLoading(false);
    }
  };

  // Calculate summary metrics
  const summaryMetrics = useMemo(() => {
    const total = announcements.length;
    const active = announcements.filter((a) => {
      const isExpired = a.expiry_date ? new Date(a.expiry_date) < new Date() : false;
      return a.is_active && !isExpired;
    }).length;
    const scheduled = announcements.filter((a) => {
      if (!a.scheduled_datetime) return false;
      const scheduledDate = new Date(a.scheduled_datetime);
      const now = new Date();
      return scheduledDate > now;
    }).length;
    // Calculate reach rate (simplified - could be based on actual views)
    const reachRate = total > 0 ? Math.round((active / total) * 100) : 0;
    return { total, active, scheduled, reachRate };
  }, [announcements]);

  // Filter announcements based on search
  const filteredAnnouncements = useMemo(() => {
    if (!search.trim()) return announcements;
    const query = search.trim().toLowerCase();
    return announcements.filter((a) => {
      const titleMatch = a.title?.toLowerCase().includes(query);
      const typeMatch = a.announcement_type?.toLowerCase().includes(query);
      const descMatch = a.description?.toLowerCase().includes(query);
      return titleMatch || typeMatch || descMatch;
    });
  }, [announcements, search]);

  // Prepare table data and columns
  const tableData = filteredAnnouncements.map((a, i) => ({
    ...a,
    srNo: i + 1,
  }));

  const getTypeColor = (type) => {
    const typeLower = (type || '').toLowerCase();
    if (typeLower.includes('new_feature') || typeLower.includes('new feature')) return '#9333EA';
    if (typeLower.includes('update')) return '#3B82F6';
    if (typeLower.includes('maintenance')) return '#F59E0B';
    return '#6b7280';
  };

  const tableColumns = [
    {
      key: 'srNo',
      label: 'Sr.No',
      sortable: false,
    },
    {
      key: 'title',
      label: 'Title',
      sortable: true,
    },
    {
      key: 'announcement_type',
      label: 'Type',
      sortable: true,
      render: (row) => {
        const typeText = row.announcement_type?.replace('_', ' ') || '-';
        const typeColor = getTypeColor(row.announcement_type);
        return <Pill color={typeColor}>{typeText}</Pill>;
      },
    },
    ...(isSuperAdmin
      ? [
          {
            key: 'org_name',
            label: 'Organization',
            sortable: true,
            render: (row) => row.org_name || 'N/A',
          },
        ]
      : []),
    {
      key: 'scheduled_datetime',
      label: 'Scheduled',
      sortable: true,
      render: (row) =>
        row.scheduled_datetime
          ? formatDate(row.scheduled_datetime, { includeTime: true })
          : '-',
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (row) => {
        const isExpired = row.expiry_date
          ? new Date(row.expiry_date) < new Date()
          : false;
        const isActuallyActive = row.is_active && !isExpired;
        const statusColor = isActuallyActive ? '#2e7d32' : '#6b7280';
        return (
          <Tooltip
            title={
              togglingId === row.announcement_id
                ? 'Updating status…'
                : isActuallyActive
                  ? 'Click to mark as Inactive'
                  : 'Click to mark as Active'
            }
            arrow
          >
            <Box
              component="span"
              onClick={() => {
                if (togglingId) return;
                handleToggleStatus(row);
              }}
              sx={{
                cursor: togglingId === row.announcement_id ? 'not-allowed' : 'pointer',
                opacity: togglingId === row.announcement_id ? 0.6 : 1,
              }}
            >
              <Pill color={statusColor}>
                {togglingId === row.announcement_id
                  ? 'Updating…'
                  : isActuallyActive
                    ? 'Active'
                    : 'Inactive'}
              </Pill>
            </Box>
          </Tooltip>
        );
      },
    },
  ];

  const renderActions = (row) => (
    <Box sx={{ display: 'flex', gap: 0.5 }}>
      <Tooltip title="View Details" arrow>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleViewClick(row.announcement_id);
          }}
        >
          <img src={ViewIcon} alt="View" style={{ width: 18, height: 18 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Edit" arrow>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleEditClick(row.announcement_id);
          }}
        >
          <img src={EditIcon} alt="Edit" style={{ width: 18, height: 18 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Delete" arrow>
        <IconButton
          size="small"
          disabled={deletingId === row.announcement_id}
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteAnnouncement(row.announcement_id);
          }}
        >
          <img
            src={DeleteIcon}
            alt="Delete"
            style={{ width: 18, height: 18 }}
          />
        </IconButton>
      </Tooltip>
    </Box>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        bgcolor: '#FAFBFD',
        minHeight: '100vh',
      }}
    >
      <Sidebar />
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <PageHeader
          title="Admin"
          createButtonText="+ Create New"
          createButtonClick={() => {
            setEditingId(null);
            setForm({
              title: '',
              type: '',
              description: '',
              when: null,
              expiry_when: null,
              is_active: true,
            });
            setErrors({});
            setFormOpen(true);
          }}
          showSearchIcon={true}
          searchIconClick={() => setSearchOpen(!searchOpen)}
          searchOpen={searchOpen}
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search announcements…"
          showConnectorIcon={false}
        />

        <Box sx={{ flexGrow: 1, px: { xs: 2, sm: 3 }, py: 2 }}>
          <AdminTabs
            items={[
              ...(isSuperAdmin
                ? [
                    {
                      to: '/admin/organizations',
                      label: 'Organizations',
                      icon: Business,
                    },
                  ]
                : []),
              { to: '/admin/user', label: 'User Management', icon: Person4 },
              {
                to: '/admin/annoucement',
                label: 'Annoucement',
                icon: Announcement,
              },
              {
                to: '/admin/setting',
                label: 'Application Settings',
                icon: SettingsRounded,
              },
              {
                to: '/admin/audit-log',
                label: 'Audit Logs',
                icon: TrackChanges,
              },
            ]}
          />

          <Box sx={{ mb: 1.5, mt: 1.5 }}>
            <Typography
              sx={{
                fontSize: { xs: '0.875rem', sm: '0.9rem' },
                fontWeight: 500,
                color: '#4A5565',
                fontFamily: 'Inter, sans-serif',
              }}
            >
              Manage all announcement and Notifications
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                fontWeight: 400,
                color: '#6B7280',
                fontFamily: 'Inter, sans-serif',
                mt: 0.5,
              }}
            >
              Note: All announcements will remain <strong>Active</strong> until the expiry date & time you specify.
            </Typography>
          </Box>

          {/* Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <SummaryCard
                title="Total Announcement"
                value={summaryMetrics.total}
                icon={Campaign}
                iconColor="#155DFC"
                iconBgColor="#E0F2FE"
                variants={itemVariants}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <SummaryCard
                title="Active Now"
                value={summaryMetrics.active}
                icon={SignalWifiStatusbar4Bar}
                iconColor="#059669"
                iconBgColor="#D1FAE5"
                variants={itemVariants}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <SummaryCard
                title="Scheduled"
                value={summaryMetrics.scheduled}
                icon={Notifications}
                iconColor="#F58E01"
                iconBgColor="#FEF9C3"
                variants={itemVariants}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <SummaryCard
                title="Reach Rate"
                value={`${summaryMetrics.reachRate}%`}
                icon={TrendingUp}
                iconColor="#9F22FC"
                iconBgColor="#F3E8FF"
                variants={itemVariants}
              />
            </Grid>
          </Grid>

          {/* Announcements Table */}
          <Box
            component={motion.div}
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <DataTable
              columns={tableColumns}
              data={tableData}
              loading={listLoading}
              showSerialNumber={false}
              showActions={true}
              actionsColumnLabel="Action"
              renderActions={renderActions}
              emptyMessage="No announcements yet."
              emptySubMessage='Click "+ Create New" to get started.'
            />
          </Box>
        </Box>

        {/* 🔹 Dialog with the SAME form (fields, styling, buttons) */}
        <Dialog
          open={formOpen}
          onClose={onCancel}
          fullWidth
          maxWidth="md"
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <Box sx={{ p: 3 }}>
            <Typography
              sx={{
                fontSize: { xs: '1rem', sm: '1.1rem' },
                fontWeight: 600,
                color: '#111827',
                mb: 2,
              }}
            >
              {isEditMode ? 'Edit Announcement' : 'New Announcement'}
            </Typography>

            <Grid container spacing={2}>
              {/* Title */}
              <Grid item xs={12} md={6}>
                <StyledTextField
                  fullWidth
                  label="Title"
                  placeholder="Enter Announcement Title"
                  value={form.title}
                  onChange={set('title')}
                  error={!!errors.title}
                  helperText={errors.title || ' '}
                />
              </Grid>

              {/* Type */}
              <Grid item xs={12} md={6}>
                <StyledTextField
                  select
                  fullWidth
                  label="Type"
                  value={form.type}
                  onChange={set('type')}
                  error={!!errors.type}
                  helperText={errors.type || ' '}
                  SelectProps={{
                    IconComponent: KeyboardArrowDownRounded,
                    MenuProps: {
                      TransitionComponent: Fade,
                    },
                  }}
                >
                  <MenuItem value="new_feature">New feature</MenuItem>
                  <MenuItem value="announcement">Announcement</MenuItem>
                  <MenuItem value="maintenance">Maintenance</MenuItem>
                </StyledTextField>
              </Grid>

              {/* Date & Time */}
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DateTimePicker
                    label="Date & Time"
                    value={form.when}
                    disablePast={!isEditMode}
                    minDateTime={!isEditMode ? dayjs() : undefined}
                    onChange={(v) => {
                      const now = dayjs();
                      if (!v || !v.isValid()) {
                        setErrors((s) => ({
                          ...s,
                          when: 'Please choose a valid date & time',
                        }));
                        setForm((s) => ({ ...s, when: null }));
                        return;
                      }
                      if (v.isBefore(now)) {
                        setErrors((s) => ({
                          ...s,
                          when: 'Past date & time not allowed',
                        }));
                        return;
                      }
                      setErrors((s) => ({ ...s, when: undefined }));
                      setForm((s) => ({ ...s, when: v }));
                      if (!form.expiry_when) {
                        const defaultExpiry = getDefaultExpiryDate(v.toDate().toISOString());
                        if (defaultExpiry) {
                          setForm((s) => ({ ...s, expiry_when: defaultExpiry }));
                        }
                      } else if (form.expiry_when.isBefore(v)) {
                        const defaultExpiry = getDefaultExpiryDate(v.toDate().toISOString());
                        if (defaultExpiry) {
                          setForm((s) => ({ ...s, expiry_when: defaultExpiry }));
                        }
                      }
                    }}
                    onError={(reason) => {
                      setErrors((s) => ({
                        ...s,
                        when: reason
                          ? 'Past date & time not allowed'
                          : undefined,
                      }));
                    }}
                    slots={{ textField: StyledTextField }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        placeholder: 'Select date and time',
                        error: !!errors.when,
                        helperText: errors.when || ' ',
                      },
                      openPickerButton: {
                        sx: {
                          width: 36,
                          height: 36,
                          m: 0.25,
                          '& .MuiSvgIcon-root': {
                            fontSize: 20,
                          },
                        },
                      },
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: 45,
                        alignItems: 'center',
                      },
                      '& .MuiInputAdornment-root': {
                        m: 0,
                      },
                      '& .MuiInputAdornment-root .MuiIconButton-root': {
                        width: 36,
                        height: 36,
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DateTimePicker
                    label="Expiry Date & Time"
                    value={form.expiry_when}
                    disablePast
                    minDateTime={form.when || dayjs()}
                    onChange={(v) => {
                      if (!v || !v.isValid()) {
                        setErrors((s) => ({
                          ...s,
                          expiry_when: 'Please choose a valid expiry date & time',
                        }));
                        setForm((s) => ({ ...s, expiry_when: null }));
                        return;
                      }
                      if (form.when && v.isBefore(form.when)) {
                        setErrors((s) => ({
                          ...s,
                          expiry_when: 'Expiry date must be after scheduled date & time',
                        }));
                        return;
                      }
                      setErrors((s) => ({ ...s, expiry_when: undefined }));
                      setForm((s) => ({ ...s, expiry_when: v }));
                    }}
                    onError={(reason) => {
                      setErrors((s) => ({
                        ...s,
                        expiry_when: reason
                          ? 'Please choose a valid expiry date & time'
                          : undefined,
                      }));
                    }}
                    slots={{ textField: StyledTextField }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        placeholder: 'Select expiry date and time',
                        error: !!errors.expiry_when,
                        helperText: errors.expiry_when || ' ',
                      },
                      openPickerButton: {
                        sx: {
                          width: 36,
                          height: 36,
                          m: 0.25,
                          '& .MuiSvgIcon-root': {
                            fontSize: 20,
                          },
                        },
                      },
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: 45,
                        alignItems: 'center',
                      },
                      '& .MuiInputAdornment-root': {
                        m: 0,
                      },
                      '& .MuiInputAdornment-root .MuiIconButton-root': {
                        width: 36,
                        height: 36,
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              {isEditMode && (
                <Grid
                  item
                  xs={12}
                  md={6}
                  component={motion.div}
                  custom={2.5}
                  variants={rowVariants}
                >
                  <StyledTextField
                    select
                    fullWidth
                    label="Status"
                    value={form.is_active ? 'active' : 'inactive'}
                    onChange={(e) => {
                      setForm((s) => ({ ...s, is_active: e.target.value === 'active' }));
                    }}
                    SelectProps={{
                      IconComponent: KeyboardArrowDownRounded,
                      MenuProps: {
                        TransitionComponent: Fade,
                      },
                    }}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </StyledTextField>
                </Grid>
              )}

              {/* Description */}
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      color: errors.description ? '#d32f2f' : '#1B2640',
                      mb: 0.5,
                      fontWeight: 400,
                    }}
                  >
                    Description
                  </Typography>
                  <Box
                    sx={{
                      '& .quill': {
                        border: errors.description
                          ? '1px solid #d32f2f'
                          : '0.5px solid #e5e7eb',
                        borderRadius: '4px',
                        '&:hover': {
                          border: errors.description
                            ? '1px solid #d32f2f'
                            : '0.5px solid #d1d5db',
                        },
                        '& .ql-toolbar': {
                          borderTopLeftRadius: '4px',
                          borderTopRightRadius: '4px',
                          borderBottom: '0.5px solid #e5e7eb',
                        },
                        '& .ql-container': {
                          borderBottomLeftRadius: '4px',
                          borderBottomRightRadius: '4px',
                          fontSize: '0.9rem',
                          minHeight: '120px',
                        },
                        '& .ql-editor': {
                          minHeight: '120px',
                          '& p, & ol, & ul': {
                            margin: '0.5em 0',
                          },
                        },
                        '&.ql-focused': {
                          border: '0.6px solid #1B2640',
                        },
                      },
                    }}
                  >
                    <ReactQuill
                      theme="snow"
                      value={form.description}
                      onChange={(value) => setForm((s) => ({ ...s, description: value }))}
                      modules={{
                        toolbar: [
                          [{ header: [1, 2, 3, false] }],
                          ['bold', 'italic', 'underline'],
                          [{ list: 'ordered' }, { list: 'bullet' }],
                          ['link'],
                          ['clean'],
                        ],
                      }}
                      formats={[
                        'header',
                        'bold',
                        'italic',
                        'underline',
                        'list',
                        'bullet',
                        'link',
                      ]}
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      color: errors.description ? '#d32f2f' : '#6b7280',
                      mt: 0.5,
                      ml: 1.5,
                    }}
                  >
                    {errors.description || `${descriptionWords}/250 words` || ' '}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Buttons */}
            <Box
              sx={{
                mt: 3,
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 1.5,
              }}
            >
              <Button
                variant="contained"
                size="small"
                sx={{
                  bgcolor: '#EEF2FF',
                  color: '#5965A6',
                  border: '1px solid #E5E7EB',
                }}
                onClick={onCancel}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                sx={{ bgcolor: '#1B2640', '&:hover': { bgcolor: '#0F1720' } }}
                onClick={isEditMode ? onUpdate : onPublish}
                disabled={loading}
              >
                {loading
                  ? isEditMode
                    ? 'Updating…'
                    : 'Publishing…'
                  : isEditMode
                    ? 'Update'
                    : 'Publish'}
              </Button>
            </Box>
          </Box>
        </Dialog>

        <Dialog
          open={viewOpen}
          onClose={() => {
            setViewOpen(false);
            setViewData(null);
          }}
          fullWidth
          maxWidth="md"
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <Box sx={{ p: 3 }}>
            <Typography
              sx={{
                fontSize: { xs: '1rem', sm: '1.1rem' },
                fontWeight: 600,
                color: '#111827',
                mb: 3,
              }}
            >
              Announcement Details
            </Typography>

            {viewData && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography sx={{ fontSize: '0.75rem', color: '#6b7280', mb: 0.5 }}>
                    Title
                  </Typography>
                  <Typography sx={{ fontSize: '0.9rem', color: '#111827', fontWeight: 500 }}>
                    {viewData.title || '-'}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography sx={{ fontSize: '0.75rem', color: '#6b7280', mb: 0.5 }}>
                    Type
                  </Typography>
                  <Typography sx={{ fontSize: '0.9rem', color: '#111827', fontWeight: 500, textTransform: 'capitalize' }}>
                    {viewData.announcement_type?.replace('_', ' ') || '-'}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography sx={{ fontSize: '0.75rem', color: '#6b7280', mb: 0.5 }}>
                    Scheduled Date & Time
                  </Typography>
                  <Typography sx={{ fontSize: '0.9rem', color: '#111827', fontWeight: 500 }}>
                    {viewData.scheduled_datetime
                      ? formatDate(viewData.scheduled_datetime, { includeTime: true })
                      : '-'}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography sx={{ fontSize: '0.75rem', color: '#6b7280', mb: 0.5 }}>
                    Expiry Date & Time
                  </Typography>
                  <Typography sx={{ fontSize: '0.9rem', color: '#111827', fontWeight: 500 }}>
                    {viewData.expiry_date
                      ? formatDate(viewData.expiry_date, { includeTime: true })
                      : '-'}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography sx={{ fontSize: '0.75rem', color: '#6b7280', mb: 0.5 }}>
                    Status
                  </Typography>
                  <Pill color={viewData.is_active ? '#2e7d32' : '#6b7280'}>
                    {viewData.is_active ? 'Active' : 'Inactive'}
                  </Pill>
                </Grid>

                {isSuperAdmin && viewData.org_name && (
                  <Grid item xs={12} md={6}>
                    <Typography sx={{ fontSize: '0.75rem', color: '#6b7280', mb: 0.5 }}>
                      Organization
                    </Typography>
                    <Typography sx={{ fontSize: '0.9rem', color: '#111827', fontWeight: 500 }}>
                      {viewData.org_name || '-'}
                    </Typography>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Typography sx={{ fontSize: '0.75rem', color: '#6b7280', mb: 0.5 }}>
                    Description
                  </Typography>
                  <Box
                    sx={{
                      fontSize: '0.9rem',
                      color: '#111827',
                      wordBreak: 'break-word',
                      '& a': {
                        color: '#1B2640',
                        textDecoration: 'underline',
                        '&:hover': {
                          color: '#3D9462',
                        },
                      },
                    }}
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtml(viewData.description || '-'),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography sx={{ fontSize: '0.75rem', color: '#6b7280', mb: 0.5 }}>
                    Created At
                  </Typography>
                  <Typography sx={{ fontSize: '0.9rem', color: '#111827', fontWeight: 500 }}>
                    {viewData.created_at
                      ? formatDate(viewData.created_at, { includeTime: true })
                      : '-'}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography sx={{ fontSize: '0.75rem', color: '#6b7280', mb: 0.5 }}>
                    Last Updated
                  </Typography>
                  <Typography sx={{ fontSize: '0.9rem', color: '#111827', fontWeight: 500 }}>
                    {viewData.updated_at
                      ? formatDate(viewData.updated_at, { includeTime: true })
                      : '-'}
                  </Typography>
                </Grid>
              </Grid>
            )}

            <Box
              sx={{
                mt: 3,
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <Button
                variant="contained"
                size="small"
                sx={{ bgcolor: '#1B2640', '&:hover': { bgcolor: '#0F1720' } }}
                onClick={() => {
                  setViewOpen(false);
                  setViewData(null);
                }}
              >
                Close
              </Button>
            </Box>
          </Box>
        </Dialog>
      </Box>
      <OnboardingTour />
    </Box>
  );
}
